import React, { useEffect, useRef, useState, useCallback } from "react";
import imageData from "../../data/imageData";

const TOTAL_CARDS = imageData.length;

const RARITY_ORDER = ["Common","Uncommon","Rare","Epic","Legendary","Mythic","Ultra"];

const RARITY_COLOURS = {
  Common:    "#9e9e9e",
  Uncommon:  "#4caf50",
  Rare:      "#2196f3",
  Epic:      "#9c27b0",
  Legendary: "#ff9800",
  Mythic:    "#ffeb3b",
  Ultra:     "#ff2d78",
};

const RARITY_GLOW = {
  Common:    "rgba(158,158,158,0.5)",
  Uncommon:  "rgba(76,175,80,0.6)",
  Rare:      "rgba(33,150,243,0.65)",
  Epic:      "rgba(156,39,176,0.7)",
  Legendary: "rgba(255,152,0,0.75)",
  Mythic:    "rgba(255,235,59,0.8)",
  Ultra:     "rgba(255,45,120,0.85)",
};

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return { r, g, b };
}

export default function ShareCard({ inventory, rollCount, totalCoins, prestige, theme = "dark", onClose }) {
  const canvasRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const isLight = theme === "light";

  // Theme colour sets
  const T = isLight ? {
    bg0:        "#e8eeff",
    bg1:        "#f0f4ff",
    bg2:        "#dde4ff",
    border0:    "rgba(100,80,200,0.5)",
    border1:    "rgba(100,80,200,0.3)",
    border2:    "rgba(0,120,204,0.45)",
    glow1c:     "rgba(120,80,255,0.10)",
    glow2c:     "rgba(0,120,200,0.08)",
    gridLine:   "rgba(0,0,0,0.04)",
    titleCol:   "#6a22d4",
    titleGlow:  "rgba(106,34,212,0.4)",
    titleLabel: "rgba(106,34,212,0.45)",
    statBg:     "rgba(0,0,0,0.04)",
    statBorder: "rgba(0,0,0,0.07)",
    statVal:    "#0077cc",
    statValGlow:"rgba(0,119,204,0.4)",
    statLabel:  "rgba(80,60,140,0.8)",
    divLine:    "rgba(0,0,0,0.08)",
    secLabel:   "rgba(80,60,140,0.7)",
    cardBg:     "rgba(255,255,255,0.92)",
    nameCol:    "#1a1040",
    footerCol:  "rgba(80,60,140,0.45)",
    prestigeBg: "rgba(106,34,212,0.1)",
    prestigeBorder:"rgba(106,34,212,0.3)",
    prestigeCol:"#6a22d4",
  } : {
    bg0:        "#0a0612",
    bg1:        "#130d1e",
    bg2:        "#0d0820",
    border0:    "rgba(255,215,0,0.6)",
    border1:    "rgba(191,95,255,0.4)",
    border2:    "rgba(0,229,255,0.5)",
    glow1c:     "rgba(191,95,255,0.18)",
    glow2c:     "rgba(0,229,255,0.12)",
    gridLine:   "rgba(255,255,255,0.025)",
    titleCol:   "#ffd700",
    titleGlow:  "rgba(255,215,0,0.7)",
    titleLabel: "rgba(255,215,0,0.5)",
    statBg:     "rgba(255,255,255,0.04)",
    statBorder: "rgba(255,255,255,0.08)",
    statVal:    "#00e5ff",
    statValGlow:"rgba(0,229,255,0.5)",
    statLabel:  "rgba(153,128,196,0.9)",
    divLine:    "rgba(255,255,255,0.12)",
    secLabel:   "rgba(153,128,196,0.8)",
    cardBg:     "rgba(19,13,30,0.95)",
    nameCol:    "#f0e6ff",
    footerCol:  "rgba(153,128,196,0.5)",
    prestigeBg: "rgba(255,215,0,0.15)",
    prestigeBorder:"rgba(255,215,0,0.35)",
    prestigeCol:"#ffd700",
  };

  // Pick top 5 rarest owned hamsters
  const topHamsters = [...inventory]
    .sort((a,b) => RARITY_ORDER.indexOf(b.rarity) - RARITY_ORDER.indexOf(a.rarity))
    .slice(0, 5);

  const completionPct = Math.round((inventory.length / TOTAL_CARDS) * 100);

  const drawCard = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = 800, H = 510;
    canvas.width  = W;
    canvas.height = H;

    // ── Background ──────────────────────────────────────────────
    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0,   T.bg0);
    bg.addColorStop(0.5, T.bg1);
    bg.addColorStop(1,   T.bg2);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Grid lines
    ctx.strokeStyle = T.gridLine;
    ctx.lineWidth = 1;
    for (let x = 0; x < W; x += 40) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke(); }
    for (let y = 0; y < H; y += 40) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke(); }

    // Top-left glow
    const glow1 = ctx.createRadialGradient(0, 0, 0, 0, 0, 400);
    glow1.addColorStop(0, T.glow1c);
    glow1.addColorStop(1, "transparent");
    ctx.fillStyle = glow1;
    ctx.fillRect(0, 0, W, H);

    // Bottom-right glow
    const glow2 = ctx.createRadialGradient(W, H, 0, W, H, 350);
    glow2.addColorStop(0, T.glow2c);
    glow2.addColorStop(1, "transparent");
    ctx.fillStyle = glow2;
    ctx.fillRect(0, 0, W, H);

    // ── Border ───────────────────────────────────────────────────
    const border = ctx.createLinearGradient(0, 0, W, H);
    border.addColorStop(0,   T.border0);
    border.addColorStop(0.5, T.border1);
    border.addColorStop(1,   T.border2);
    ctx.strokeStyle = border;
    ctx.lineWidth = 2;
    roundRect(ctx, 2, 2, W-4, H-4, 24);
    ctx.stroke();

    // ── Header ───────────────────────────────────────────────────
    ctx.font = "bold 13px 'Arial'";
    ctx.fillStyle = T.titleLabel;
    ctx.fillText("🎰  HAMSTER GAMBLER", 32, 42);

    // Title
    ctx.font = "bold 38px 'Arial'";
    ctx.fillStyle = T.titleCol;
    ctx.shadowColor = T.titleGlow;
    ctx.shadowBlur = 20;
    ctx.fillText("My Collection", 32, 88);
    ctx.shadowBlur = 0;

    // Prestige badge
    if (prestige > 0) {
      ctx.fillStyle = T.prestigeBg;
      roundRect(ctx, 32, 100, 120, 26, 13);
      ctx.fill();
      ctx.strokeStyle = T.prestigeBorder;
      ctx.lineWidth = 1;
      roundRect(ctx, 32, 100, 120, 26, 13);
      ctx.stroke();
      ctx.fillStyle = T.prestigeCol;
      ctx.font = "bold 12px 'Arial'";
      ctx.fillText(`⭐ Prestige ${prestige}`, 44, 118);
    }

    // ── Stats row ────────────────────────────────────────────────
    const stats = [
      { label: "ROLLS",        value: rollCount.toLocaleString() },
      { label: "COLLECTED",    value: `${inventory.length} / ${TOTAL_CARDS}` },
      { label: "COMPLETION",   value: `${completionPct}%` },
      { label: "COINS EARNED", value: totalCoins.toLocaleString() },
    ];

    const statsY = 148;
    stats.forEach((s, i) => {
      const x = 32 + i * 185;
      ctx.fillStyle = T.statBg;
      roundRect(ctx, x, statsY, 170, 52, 10);
      ctx.fill();
      ctx.strokeStyle = T.statBorder;
      ctx.lineWidth = 1;
      roundRect(ctx, x, statsY, 170, 52, 10);
      ctx.stroke();
      ctx.font = "bold 22px 'Arial'";
      ctx.fillStyle = T.statVal;
      ctx.shadowColor = T.statValGlow;
      ctx.shadowBlur = 10;
      ctx.fillText(s.value, x + 12, statsY + 28);
      ctx.shadowBlur = 0;
      ctx.font = "bold 10px 'Arial'";
      ctx.fillStyle = T.statLabel;
      ctx.fillText(s.label, x + 12, statsY + 44);
    });

    // ── Divider ──────────────────────────────────────────────────
    const divGrad = ctx.createLinearGradient(32, 0, W - 32, 0);
    divGrad.addColorStop(0,   "transparent");
    divGrad.addColorStop(0.3, T.divLine);
    divGrad.addColorStop(0.7, T.divLine);
    divGrad.addColorStop(1,   "transparent");
    ctx.strokeStyle = divGrad;
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(32, 218); ctx.lineTo(W-32, 218); ctx.stroke();

    // ── Top hamsters label ───────────────────────────────────────
    ctx.font = "bold 11px 'Arial'";
    ctx.fillStyle = T.secLabel;
    ctx.fillText("TOP PULLS", 32, 242);

    // ── Hamster cards ────────────────────────────────────────────
    const cardW = 140, cardH = 195, cardY = 252;

    if (topHamsters.length === 0) {
      ctx.font = "bold 16px 'Arial'";
      ctx.fillStyle = T.secLabel;
      ctx.textAlign = "center";
      ctx.fillText("No hamsters collected yet — start rolling!", W / 2, cardY + 80);
      ctx.textAlign = "left";
      setIsReady(true);
      return;
    }

    const totalW = topHamsters.length * cardW + (topHamsters.length - 1) * 12;
    const startX = (W - totalW) / 2;

    const imagePromises = topHamsters.map(h => new Promise(resolve => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload  = () => resolve({ img, hamster: h });
      img.onerror = () => resolve({ img: null, hamster: h });
      img.src = h.image;
    }));

    const loaded = await Promise.all(imagePromises);

    loaded.forEach(({ img, hamster }, i) => {
      const x = startX + i * (cardW + 12);
      const col = RARITY_COLOURS[hamster.rarity] || "#fff";
      const glowCol = RARITY_GLOW[hamster.rarity] || "rgba(255,255,255,0.5)";
      const rgb = hexToRgb(col);

      // Card glow
      ctx.shadowColor = glowCol;
      ctx.shadowBlur = 22;
      ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},0.08)`;
      roundRect(ctx, x, cardY, cardW, cardH, 14);
      ctx.fill();
      ctx.shadowBlur = 0;

      // Card bg
      ctx.fillStyle = T.cardBg;
      roundRect(ctx, x, cardY, cardW, cardH, 14);
      ctx.fill();

      // Rarity border
      ctx.strokeStyle = col;
      ctx.lineWidth = 2;
      roundRect(ctx, x, cardY, cardW, cardH, 14);
      ctx.stroke();

      // Top rarity bar
      ctx.fillStyle = col;
      roundRectTop(ctx, x, cardY, cardW, 4, 14);
      ctx.fill();

      // Image
      const imgSize = 100, imgX = x + (cardW - imgSize) / 2, imgY = cardY + 14;
      ctx.save();
      ctx.beginPath();
      roundRect(ctx, imgX, imgY, imgSize, imgSize, 10);
      ctx.clip();
      if (img) {
        ctx.drawImage(img, imgX, imgY, imgSize, imgSize);
      } else {
        ctx.fillStyle = "rgba(128,128,128,0.3)";
        ctx.fillRect(imgX, imgY, imgSize, imgSize);
        ctx.fillStyle = "#666";
        ctx.font = "28px Arial";
        ctx.fillText("🐹", imgX + 28, imgY + 50);
      }
      ctx.restore();

      // Name — smart two-line wrap
      ctx.textAlign = "center";
      ctx.fillStyle = T.nameCol;
      const maxW = cardW - 10;
      const words = hamster.name.split(" ");
      let fontSize = 11;
      ctx.font = `bold ${fontSize}px 'Arial'`;
      // shrink font until full name fits on one line or we split to two
      while (ctx.measureText(hamster.name).width > maxW && fontSize > 8) {
        fontSize--;
        ctx.font = `bold ${fontSize}px 'Arial'`;
      }
      if (ctx.measureText(hamster.name).width <= maxW) {
        ctx.fillText(hamster.name, x + cardW / 2, cardY + 130);
      } else {
        // split into two lines at best word boundary
        let line1 = "", line2 = "";
        let splitIdx = words.length;
        for (let w = 1; w <= words.length; w++) {
          const attempt = words.slice(0, w).join(" ");
          if (ctx.measureText(attempt).width > maxW) { splitIdx = w - 1; break; }
        }
        if (splitIdx === 0) splitIdx = 1;
        line1 = words.slice(0, splitIdx).join(" ");
        line2 = words.slice(splitIdx).join(" ");
        // shrink further if line2 still overflows
        while (ctx.measureText(line2).width > maxW && fontSize > 7) {
          fontSize--;
          ctx.font = `bold ${fontSize}px 'Arial'`;
        }
        ctx.fillText(line1, x + cardW / 2, cardY + 124);
        ctx.fillText(line2, x + cardW / 2, cardY + 137);
      }

      // Rarity badge
      ctx.font = "bold 9px 'Arial'";
      ctx.fillStyle = col;
      ctx.shadowColor = glowCol;
      ctx.shadowBlur = 8;
      ctx.fillText(`✦ ${hamster.rarity.toUpperCase()}`, x + cardW/2, cardY + 152);
      ctx.shadowBlur = 0;

      // Crown for top card
      if (i === 0) {
        ctx.textAlign = "left";
        ctx.font = "bold 11px 'Arial'";
        ctx.fillStyle = "#ffd700";
        ctx.shadowColor = "rgba(255,215,0,0.8)";
        ctx.shadowBlur = 8;
        ctx.fillText("👑", x + 4, cardY + 172);
        ctx.shadowBlur = 0;
      }

      ctx.textAlign = "left";
    });

    // ── Footer ───────────────────────────────────────────────────
    ctx.font = "bold 11px 'Arial'";
    ctx.fillStyle = T.footerCol;
    ctx.fillText("hamstergambler.com  ·  Collect · Roll · Flex", 32, H - 18);

    // Timestamp
    const now = new Date();
    const dateStr = now.toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" });
    ctx.textAlign = "right";
    ctx.fillText(dateStr, W - 32, H - 18);
    ctx.textAlign = "left";

    setIsReady(true);
  }, [inventory, rollCount, totalCoins, prestige, completionPct, topHamsters, theme]); // eslint-disable-line

  useEffect(() => { drawCard(); }, [drawCard]);

  const handleDownload = () => {
    setIsDownloading(true);
    const canvas = canvasRef.current;
    const link = document.createElement("a");
    link.download = "hamster-collection.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
    setTimeout(() => setIsDownloading(false), 1000);
  };

  const handleShare = async () => {
    const canvas = canvasRef.current;
    canvas.toBlob(async (blob) => {
      const file = new File([blob], "hamster-collection.png", { type: "image/png" });
      if (navigator.share && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "My Hamster Collection", text: `${completionPct}% complete · ${rollCount} rolls` });
        } catch {}
      } else {
        handleDownload();
      }
    });
  };

  return (
    <div className="sharecard-overlay" onClick={onClose}>
      <div className={`sharecard-modal${isLight ? " sharecard-modal--light" : ""}`} onClick={e => e.stopPropagation()}>
        <button className="sharecard-close" onClick={onClose}>✕</button>
        <h2 className="sharecard-title">📸 Share Your Collection</h2>
        <p className="sharecard-sub">Show off your rarest pulls</p>

        <div className="sharecard-canvas-wrap">
          <canvas ref={canvasRef} className="sharecard-canvas" />
          {!isReady && (
            <div className="sharecard-loading">
              <div className="sharecard-spinner"/>
              <p>Generating card…</p>
            </div>
          )}
        </div>

        <div className="sharecard-actions">
          <button className="sharecard-btn sharecard-btn-download" onClick={handleDownload} disabled={!isReady}>
            {isDownloading ? "✅ Saved!" : "⬇️ Download PNG"}
          </button>
          <button className="sharecard-btn sharecard-btn-share" onClick={handleShare} disabled={!isReady}>
            🔗 Share
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Canvas helpers ────────────────────────────────────────────
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function roundRectTop(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h);
  ctx.lineTo(x, y + h);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}